/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import { bool, func, arrayOf, shape } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { isIOS } from "react-device-detect";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { alphabeticalSorting } from "util/servicePayments";
import { actions as notificationActions } from "reducers/notification";
import { actions as afipPaymentLinkActions, selectors as afipPaymentLinkSelectors } from "reducers/afipPaymentLink";
import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";
import Selector from "pages/_components/fields/formik/Selector";

const FORM_ID = "add.service.link";

const AddService = ({ isDesktop, fetching, dispatch, categories, entities }) => {
    const formRef = useRef(null);
    const [alphanumericValidation, setAlphanumericValidation] = useState("");
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [entityOptions, setEntityOptions] = useState([]);

    useEffect(() => {
        if (categories.length > 0) {
            const aux = categories.map(({ id, description }) => ({
                value: id,
                label: description,
            }))
            setCategoryOptions(alphabeticalSorting(aux))
        }
    }, [categories]);

    useEffect(() => {
        if (entities.length > 0) {
            const aux = entities.map(({ id, description }) => ({
                value: id,
                label: description,
            }))
            setEntityOptions(alphabeticalSorting(aux))
        }
    }, [entities]);

    const handleResetEntitySelector = () => {
        formRef.current.setFieldValue("entity", "");
    };

    const handleReset = () => {
        handleResetEntitySelector();
        formRef.current.setFieldValue("category", "");
        formRef.current.setFieldValue("paymentCode", "");
        formRef.current.setFieldValue("alias", "");
    };

    useEffect(() => {
        handleReset();
        const alphanumericWithSpacesValidation = configUtils.get("alphanumeric.blankSpaces.validationFormat", /^[a-zA-Z0-9\s]*$/);
        setAlphanumericValidation(alphanumericWithSpacesValidation);
        dispatch(afipPaymentLinkActions.addServicePaymentPre());
    }, []);

    useEffect(() => {
        if (selectedCategoryId) {
            handleResetEntitySelector();
            dispatch(afipPaymentLinkActions.addServicePaymentPre({ categoryId: selectedCategoryId }));
        }
    }, [selectedCategoryId]);

    const initialValues = {
        category: null,
        entity: null,
        paymentCode: null,
        alias: null,
    };

    const handleBack = () => {
        dispatch(push("/servicesPayment"));
        handleReset();
    };

    const renderHeader = () => (
        <>
            <CustomProductHead
                titleId=""
                backLinkTo="/servicesPayment"
                className="accounts"
                onBack={() => handleBack()}>
                <div className="toolbar-item view-title">
                    <ViewTitle id="services.payment.link.schedule.services.menu" defaultValue="Adherir servicio" />
                </div>
            </CustomProductHead>
        </>
    );

    const validation = Yup.lazy(() => {
        const validate = {
            category: Yup.string().required(i18n.get("global.error.required")).nullable(),
            entity: Yup.string().required(i18n.get("global.error.required")).nullable(),
            paymentCode: Yup.string().required(i18n.get("global.error.required")),
            alias: Yup.string()
                .notRequired()
                .matches(
                    alphanumericValidation,
                    i18n.get(
                        "forms.validation.alphanumeric.invalid.format",
                        "El campo ingresado debe ser alfanumérico",
                    ),
                ),
        };
        return Yup.object().shape(validate);
    });

    const handleSubmit = (values) => {
        const { category, entity, paymentCode, alias } = values;
        const categoryIdx = categories.findIndex((c) => c.id === category);
        const categoryName = categoryIdx !== -1 ? categories[categoryIdx].description : "-";
        const entityIdx = entities.findIndex((e) => e.id === entity);
        const entityName = entityIdx !== -1 ? entities[entityIdx].description : "-";
        const servicePaymentData = {
            categoryId: category,
            entityId: entity,
            paymentCode,
            categoryName,
            entityName,
            alias,
        };
        dispatch(afipPaymentLinkActions.setServicesPaymentData(servicePaymentData));
        dispatch(afipPaymentLinkActions.verifyServiceSchedule(servicePaymentData));
    };

    const continueIsNotAllowed = (values) => {
        const { category, entity, paymentCode } = values;
        return !category || !entity || !paymentCode;
    };

    const placeholderCategory = isDesktop ? "Seleccioná el rubro que corresponda" : "Seleccioná el rubro";
    const placeholderCategoryId = isDesktop ? "services.payment.link.category.selector.placeholder" : "";
    const placeholderAlias = isDesktop ? "Ingresá el Alias con el que vas a agendar el Servicio" : "Ingresá el Alias";
    const placeholderAliasId = isDesktop ? "services.payment.link.alias.placeholder" : "";

    return (
        <>
            <Notification scopeToShow="servicesPayment" />
            {renderHeader()}
            <MainContainer>
                <div className="container">
                    <Formik
                        initialValues={initialValues}
                        ref={formRef}
                        validationSchema={validation}
                        onSubmit={(values, formikBag) => handleSubmit(values, formikBag)}>
                        {(allFormik) => (
                            <Form className="box box-shadow">
                                <Row style={{ display: "flex", alignContent: "space-between", padding: "10px" }}>
                                    <Col xs={12} md={4}>
                                        <Row>
                                            <Col xs={12} md={12}>
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="category"
                                                    options={
                                                        categoryOptions
                                                            ? categoryOptions
                                                            : []
                                                    }
                                                    label={i18n.get(
                                                        "services.payment.link.category.selector.label",
                                                        "Rubro",
                                                    )}
                                                    placeholder={i18n.get(placeholderCategoryId, placeholderCategory)}
                                                    onChange={(value) => setSelectedCategoryId(value)}
                                                    searchable={!isIOS}
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col xs={12} md={12}>
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="entity"
                                                    options={
                                                        entityOptions
                                                            ? entityOptions
                                                            : []
                                                    }
                                                    label={i18n.get(
                                                        "services.payment.link.entity.selector.label",
                                                        "Empresa",
                                                    )}
                                                    placeholder={i18n.get(
                                                        "services.payment.link.entity.selector.placeholder",
                                                        "Elegí la empresa",
                                                    )}
                                                    disabled={!selectedCategoryId || fetching}
                                                    searchable={!isIOS}
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col xs={12} md={12}>
                                                <Field
                                                    component={TextField}
                                                    labelText="services.payment.link.paymentCode.label"
                                                    labelTextDefault="Código de pago"
                                                    placeholderText={i18n.get(
                                                        "services.payment.link.paymentCode.placeholder",
                                                        "Introducí el código de pago",
                                                    )}
                                                    idForm={FORM_ID}
                                                    name="paymentCode"
                                                    type="text"
                                                    pattern="[0-9]*"
                                                    maxLength={50}
                                                    minLength={0}
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col xs={12} md={12}>
                                                <Field
                                                    component={TextField}
                                                    labelText="services.payment.link.alias.label"
                                                    labelTextDefault="Alias (opcional)"
                                                    placeholderText={i18n.get(placeholderAliasId, placeholderAlias)}
                                                    idForm={FORM_ID}
                                                    name="alias"
                                                    type="text"
                                                    maxLength={25}
                                                    minLength={0}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                <div className="grid-table form-actions">
                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                        <div className="grid-table-data md:col-start-5 md:col-span-4">
                                            <Button
                                                className="btn btn-primary btn-block small"
                                                type="submit"
                                                label="global.continue"
                                                loading={fetching}
                                                block
                                                disabled={continueIsNotAllowed(allFormik.values)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>
        </>
    );
};

AddService.propTypes = {
    isDesktop: bool.isRequired,
    dispatch: func.isRequired,
    fetching: bool.isRequired,
    categories: arrayOf(shape({})).isRequired,
    entities: arrayOf(shape({})).isRequired,
};

const mapStateToProps = (state) => ({
    fetching: afipPaymentLinkSelectors.getFetching(state),
    categories: afipPaymentLinkSelectors.getCategories(state),
    entities: afipPaymentLinkSelectors.getEntities(state),
});

export default connect(mapStateToProps)(AddService);
